import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, CircularProgress } from '@mui/material';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { useDispatch } from 'react-redux';
import axios from 'src/utils/axios';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import { addNotification } from 'src/actions/notificationActions';
import { NOTIFICATION_STATUS } from 'src/constants';

// "Look it up" - the support person asks for the account investigation on
// the current draft, instead of waiting for a confidence threshold or a
// detector that (measured 16 Sep 2026) could not be built to fire right.
//
// Lives in the Send-to row as a LABELLED chip beside the "AI draft" chip,
// not as a fourth icon in the toolbar group: three discoverability failures
// on this panel (Use it, the withheld draft, the Apply refusal) were all
// small grey icons nobody found. Rendered on the support account only, and
// whenever there is a current draft - including the no-reply and cleared
// cases, which are exactly the ones where escalation never ran and the
// research panel therefore has nothing to show.
//
// States, from the research endpoint's currentDraft:
//   no run yet      -> "Look it up"          (one click, starts it)
//   pending         -> "Looking it up..."    (disabled, spinner)
//   run exists      -> "Look it up again"    (explicit confirm: a run costs
//                                             money and the note already
//                                             answers; re-running replaces
//                                             the note and the offered draft)
function AiLookup({
  contactId = null,
  refreshKey = '',
  onStateChanged = () => {},
}) {
  const dispatch = useDispatch();
  const [state, setState] = useState(null); // { enabled, currentDraft }
  const [isStarting, setIsStarting] = useState(false);
  const [confirmAgain, setConfirmAgain] = useState(false);

  const load = useCallback(async () => {
    if (!contactId) { setState(null); return; }
    try {
      const { data } = await axios.get('/api/ai-support-drafts/research', { params: { contactId } });
      setState(data?.enabled ? { currentDraft: data.currentDraft || null } : null);
    } catch (error) {
      setState(null);
    }
  }, [contactId]);

  // refreshKey is bumped by the panel (dismiss / restore / a new draft or
  // note arriving), so the chip's state follows the panel's without a reload.
  useEffect(() => {
    setConfirmAgain(false);
    load();
  }, [load, refreshKey]);

  const start = async (force = false) => {
    if (!contactId || isStarting) return;
    setIsStarting(true);
    try {
      await axios.post('/api/ai-support-drafts/research/request', { contactId, force });
      setState((prev) => (prev ? {
        ...prev,
        currentDraft: { ...(prev.currentDraft || {}), escalationStatus: 'pending' },
      } : prev));
      setConfirmAgain(false);
      // The panel above polls while a run is pending; this makes it look now.
      onStateChanged();
    } catch (error) {
      const data = error?.response?.data;
      if (data?.reason === 'already-looked-up') {
        setConfirmAgain(true);
      } else {
        dispatch(addNotification(
          'ai lookup',
          data?.message || 'Could not start the lookup.',
          NOTIFICATION_STATUS.ERROR,
        ));
      }
      // Whatever refused us, the server's state is the truth - refetch it.
      load();
    } finally {
      setIsStarting(false);
    }
  };

  if (!state?.currentDraft) return null;

  const status = state.currentDraft.escalationStatus;
  const isPending = status === 'pending' || isStarting;
  const hasRun = Boolean(status) && status !== 'pending';

  let label = 'Look it up';
  if (isPending) label = 'Looking it up...';
  else if (hasRun) label = 'Look it up again';

  return (
    <>
      <Chip
        icon={isPending
          ? <CircularProgress size={14} color="inherit" />
          : <ManageSearchIcon />}
        label={label}
        size="small"
        color="secondary"
        variant={hasRun ? 'outlined' : 'filled'}
        clickable={!isPending}
        disabled={isPending}
        onClick={() => (hasRun ? setConfirmAgain(true) : start(false))}
        title={hasRun
          ? 'This draft has been looked up already. Click to run it again.'
          : 'Look up this consultant\'s account and write a research note for this draft.'}
        sx={{ marginRight: '8px' }}
      />
      <ModalDialogMUI6
        isVisible={confirmAgain}
        title="Look it up again?"
        okText="Run it again"
        closeText="Cancel"
        onOk={() => start(true)}
        onClose={() => setConfirmAgain(false)}
        content={(
          <span>
            This draft already has a research note. Running the lookup again costs
            another investigation and replaces the note and the offered draft with
            new ones.
          </span>
        )}
      />
    </>
  );
}

AiLookup.propTypes = {
  contactId: PropTypes.number,
  // Composite string bumped by the panel; see AiResearchNoteRestore.
  refreshKey: PropTypes.string,
  // Called when a run is started, so the panel above refetches and shows
  // "Looking into this account..." straight away.
  onStateChanged: PropTypes.func,
};

export default AiLookup;
